import { useEffect, useState, type FormEvent } from "react";
import { Plus, Check, Download } from "lucide-react";
import { api, post, fmt, type Row } from "./api";

type Data = Record<string, Row[]>;

export function DistributionEditor({
  value,
  onChange,
  total,
  data,
}: {
  value: Row[];
  onChange: (rows: Row[]) => void;
  total: string;
  data: Data;
}) {
  const [mode, setMode] = useState("porcentaje");
  const currentMode = value.length
    ? value[0].porcentaje !== undefined
      ? "porcentaje"
      : "importe"
    : mode;
  const update = (index: number, field: string, text: string) =>
    onChange(
      value.map((r, i) =>
        i === index
          ? {
              ...r,
              [field]:
                field === "centro_costo" || field === "proyecto"
                  ? text
                    ? Number(text)
                    : null
                  : text,
            }
          : r,
      ),
    );
  const options = (key: string, selected: any) =>
    (data[key] || []).filter((r) => r.activo || r.id === selected);
  return (
    <section
      className="allocation-editor"
      aria-label="Distribución por centro de costo y proyecto"
    >
      <div className="line-heading">
        <div>
          <h3>Centros de costos y proyectos</h3>
          <p>Distribuye {fmt(total)} entre uno o varios destinos.</p>
        </div>
        <button
          className="btn secondary"
          type="button"
          onClick={() =>
            onChange([
              ...value,
              {
                centro_costo: null,
                proyecto: null,
                [currentMode]: value.length
                  ? "0"
                  : currentMode === "porcentaje"
                    ? "100"
                    : total,
              },
            ])
          }
        >
          <Plus size={14} /> Agregar destino
        </button>
      </div>
      {!value.length ? (
        <p className="muted">
          Sin asignación. Puedes clasificar todo el importe o repartirlo.
        </p>
      ) : (
        <>
          <label className="allocation-mode">
            <span>Distribuir por</span>
            <select
              aria-label="Distribuir por"
              value={currentMode}
              onChange={(e) => {
                setMode(e.target.value);
                onChange(
                  value.map((r, i) => ({
                    centro_costo: r.centro_costo,
                    proyecto: r.proyecto,
                    [e.target.value]: i
                      ? "0"
                      : e.target.value === "porcentaje"
                        ? "100"
                        : total,
                  })),
                );
              }}
            >
              <option value="porcentaje">Porcentajes</option>
              <option value="importe">Importes</option>
            </select>
          </label>
          {value.map((r, i) => (
            <div className="repeat-row" key={i}>
              <div className="form-grid">
                <label>
                  <span>Centro de costo {i + 1}</span>
                  <select
                    aria-label={`Centro de costo ${i + 1}`}
                    value={r.centro_costo ?? ""}
                    onChange={(e) => update(i, "centro_costo", e.target.value)}
                  >
                    <option value="">Sin centro de costo</option>
                    {options("centros-costo", r.centro_costo).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.codigo} · {c.nombre}
                        {!c.activo ? " (inactivo)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Proyecto {i + 1}</span>
                  <select
                    aria-label={`Proyecto ${i + 1}`}
                    value={r.proyecto ?? ""}
                    onChange={(e) => update(i, "proyecto", e.target.value)}
                  >
                    <option value="">Sin proyecto</option>
                    {options("proyectos", r.proyecto).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.codigo} · {p.nombre}
                        {!p.activo ? " (inactivo)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>
                    {currentMode === "porcentaje" ? "Porcentaje" : "Importe"}{" "}
                    {i + 1}
                  </span>
                  <input
                    aria-label={`${currentMode === "porcentaje" ? "Porcentaje" : "Importe"} ${i + 1}`}
                    inputMode="decimal"
                    required
                    value={r[currentMode] || ""}
                    onChange={(e) => update(i, currentMode, e.target.value)}
                  />
                </label>
                <button
                  className="text-button"
                  type="button"
                  onClick={() => onChange(value.filter((_, n) => n !== i))}
                >
                  Quitar destino {i + 1}
                </button>
              </div>
            </div>
          ))}
          <div className="notice">
            <div>
              {currentMode === "porcentaje"
                ? "Los porcentajes deben sumar 100 %. El sistema distribuye los centavos restantes conservando el total."
                : `Los importes deben sumar ${fmt(total)}.`}
              <p>
                Una misma combinación de centro y proyecto se registra una sola
                vez.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-button"
            onClick={() => onChange([])}
          >
            Dejar sin asignación
          </button>
        </>
      )}
    </section>
  );
}

export function DistributionForm({
  doc,
  base,
  data,
  onComplete,
}: {
  doc: Row;
  base: string;
  data: Data;
  onComplete: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api(`${base}comprobantes/${doc.id}/detalle/`)
      .then((r) => {
        if (!cancelled)
          setRows(
            r.distribucion.map((p: Row) => ({
              centro_costo: p.centro_costo,
              proyecto: p.proyecto,
              importe: p.importe,
            })),
          );
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [base, doc.id]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await post(`${base}comprobantes/${doc.id}/distribuir/`, {
        distribucion: rows,
      });
      onComplete();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit}>
      <p>
        {doc.concepto} · Base sin impuestos: <strong>{fmt(doc.base)}</strong>
      </p>
      <DistributionEditor
        value={rows}
        onChange={setRows}
        total={doc.base}
        data={data}
      />
      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}
      <div className="form-footer">
        <span>La distribución se aplicará al contabilizar.</span>
        <button className="btn" disabled={busy}>
          <Check size={16} />
          Guardar distribución
        </button>
      </div>
    </form>
  );
}

export function AnaliticaPage({
  data,
  base,
  onChanged,
  onReport,
  tab,
  onTab,
}: {
  data: Data;
  base: string;
  onChanged: () => void;
  onReport: (kind: string, id?: string) => void;
  tab: string;
  onTab: (tab: string) => void;
}) {
  const [editing, setEditing] = useState<Row | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const centers = tab === "centros-costo";
  const rows = (data[tab] || []).filter((r) =>
    `${r.codigo} ${r.nombre}`.toLowerCase().includes(search.toLowerCase()),
  );
  const update = (key: string, value: any) =>
    setEditing((r) => ({ ...r, [key]: value }));
  const field = (
    key: string,
    label: string,
    type = "text",
    required = false,
  ) => (
    <label>
      <span>
        {label}
        {required ? " *" : ""}
      </span>
      <input
        type={type}
        required={required}
        value={editing?.[key] || ""}
        onChange={(e) => update(key, e.target.value)}
      />
    </label>
  );
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    const payload: Row = {
      codigo: editing.codigo,
      nombre: editing.nombre,
      descripcion: editing.descripcion || "",
      activo: editing.activo ?? true,
    };
    if (centers) payload.padre = editing.padre ? Number(editing.padre) : null;
    else
      Object.assign(payload, {
        cliente: editing.cliente ? Number(editing.cliente) : null,
        fecha_inicio: editing.fecha_inicio || null,
        fecha_fin: editing.fecha_fin || null,
      });
    try {
      await api(`${base}${tab}/${editing.id ? `${editing.id}/` : ""}`, {
        method: editing.id ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });
      setEditing(null);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function toggle(r: Row) {
    setError("");
    try {
      await api(`${base}${tab}/${r.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ activo: !r.activo }),
      });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Organiza tus áreas y trabajos</h2>
            <p>
              El centro identifica el área; el proyecto identifica el trabajo.
              Puedes utilizar ambos.
            </p>
          </div>
          <button
            className="btn secondary"
            onClick={() => onReport("resultados-analiticos")}
          >
            <Download size={15} />
            Ver resultados
          </button>
        </div>
        <div className="analytics-tabs">
          {[
            ["centros-costo", "Centros de costos"],
            ["proyectos", "Proyectos"],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`btn ${tab === id ? "" : "secondary"}`}
              onClick={() => {
                onTab(id);
                setEditing(null);
                setError("");
                setSearch("");
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="toolbar">
          <label className="search">
            <input
              aria-label="Buscar centro o proyecto"
              placeholder="Buscar por código o nombre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <button
            className="btn"
            onClick={() => {
              setEditing({ codigo: "", nombre: "", activo: true });
              setError("");
            }}
          >
            <Plus size={16} />
            {centers ? "Nuevo centro de costo" : "Nuevo proyecto"}
          </button>
        </div>
        {error && (
          <div className="alert error" role="alert">
            {error}
          </div>
        )}
        {editing && (
          <form className="analytics-master-form" onSubmit={save}>
            <h3>
              {editing.id ? "Editar" : "Crear"}{" "}
              {centers ? "centro de costo" : "proyecto"}
            </h3>
            <div className="form-grid">
              {field("codigo", "Código", "text", true)}
              {field("nombre", "Nombre", "text", true)}
              {field("descripcion", "Descripción")}
              {centers ? (
                <label>
                  <span>Centro padre (opcional)</span>
                  <select
                    value={editing.padre || ""}
                    onChange={(e) => update("padre", e.target.value)}
                  >
                    <option value="">Sin centro padre</option>
                    {(data["centros-costo"] || [])
                      .filter((c) => c.id !== editing.id)
                      .map((c) => (
                        <option value={c.id} key={c.id}>
                          {c.codigo} · {c.nombre}
                        </option>
                      ))}
                  </select>
                </label>
              ) : (
                <>
                  <label>
                    <span>Cliente (opcional)</span>
                    <select
                      aria-label="Cliente (opcional)"
                      value={editing.cliente || ""}
                      onChange={(e) => update("cliente", e.target.value)}
                    >
                      <option value="">Sin cliente asociado</option>
                      {(data.terceros || [])
                        .filter((t) => t.cliente)
                        .map((t) => (
                          <option value={t.id} key={t.id}>
                            {t.nombre}
                          </option>
                        ))}
                    </select>
                  </label>
                  {field("fecha_inicio", "Fecha de inicio", "date")}
                  {field("fecha_fin", "Fecha de fin", "date")}
                </>
              )}
              <label>
                <span>Estado</span>
                <select
                  value={editing.activo ? "1" : "0"}
                  onChange={(e) => update("activo", e.target.value === "1")}
                >
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </label>
            </div>
            <div className="form-footer">
              <button
                className="btn secondary"
                type="button"
                onClick={() => setEditing(null)}
              >
                Cancelar
              </button>
              <button className="btn" disabled={busy}>
                Guardar
              </button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>{centers ? "Centro padre" : "Cliente / fechas"}</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <code>{r.codigo}</code>
                  </td>
                  <td>
                    <strong>{r.nombre}</strong>
                    <small>{r.descripcion}</small>
                  </td>
                  <td>
                    {centers ? (
                      r.padre_nombre || "—"
                    ) : (
                      <>
                        {(data.terceros || []).find((t) => t.id === r.cliente)
                          ?.nombre || "—"}
                        <small>
                          {r.fecha_inicio || ""}
                          {r.fecha_fin ? ` → ${r.fecha_fin}` : ""}
                        </small>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${r.activo ? "green" : "neutral"}`}>
                      {r.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="row-actions">
                    <button onClick={() => setEditing({ ...r })}>Editar</button>
                    <button onClick={() => toggle(r)}>
                      {r.activo ? "Desactivar" : "Activar"}
                    </button>
                    <button
                      onClick={() =>
                        onReport(
                          centers
                            ? "resultados-centros"
                            : "resultados-proyectos",
                          String(r.id),
                        )
                      }
                    >
                      Ver resultados
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={5}>
                    Todavía no hay {centers ? "centros de costos" : "proyectos"}{" "}
                    registrados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="panel-footer">
          Los registros inactivos conservan su historial y dejan de ofrecerse
          para nuevos movimientos.
        </div>
      </section>
    </>
  );
}
